/**
 * Cross-domain attribution capture.
 *
 * Captures UTM parameters and referrer on first arrival and persists
 * them in a first-party cookie on the parent domain (.newsquawk.com),
 * exactly like `clickIds.ts`. This lets us attribute every login,
 * signup intent, and Chargebee checkout to the original traffic source
 * — including journeys that start on the marketing site and finish in
 * the portal days later.
 *
 * A cookie (not sessionStorage) is deliberate. sessionStorage is scoped
 * per-origin and dies with the tab, so it could never carry a source
 * from newsquawk.com to app.newsquawk.com, and lost attribution for
 * anyone who didn't convert in the same sitting. Trials rarely start
 * and finish in one session.
 *
 * Keycloak uses `check-sso` (silent iframe), so unauthenticated/demo
 * users are never redirected — URL params stay intact. The full
 * redirect only happens when a user explicitly clicks "Login", but by
 * then captureAttribution() has already run at module level.
 *
 * Domains involved:
 *   - newsquawk.com        (marketing / SEO landing pages)
 *   - app.newsquawk.com    (this app — demo + paid portal)
 *
 * This module is intentionally duplicated in the marketing repo
 * (marketing-and-news-app). Both write the same cookie in the same
 * format; keep them in sync.
 */

const COOKIE_NAME = "nq_attr";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days — matches clickIds.ts
const MAX_LANDING_PAGE_LENGTH = 200;

/** Our own hosts — a referral from one of these isn't an acquisition source. */
const OWN_DOMAINS = [
  "newsquawk.com",
  "www.newsquawk.com",
  "app.newsquawk.com",
  "auth.newsquawk.com",
  "auth.uat.newsquawk.com",
  "legacy.newsquawk.com",
];

/**
 * How strong the stored signal is.
 *
 * `campaign` — the landing URL carried explicit utm_* params.
 * `referrer` — we only know the referring domain.
 *
 * A `campaign` capture supersedes a stored `referrer` one (see
 * captureAttribution). Without that rule someone who arrived via a
 * shared link months ago would keep that stale source forever, even
 * after clicking a paid ad — and would then disagree with the gclid
 * cookie, which is a genuinely confusing pair of records to debug.
 */
export type AttributionSignal = "campaign" | "referrer";

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  /** The referring domain only (e.g. "google.com") */
  referrer_domain?: string;
  /** Host + path of the first page they landed on (query stripped) */
  landing_page?: string;
  /** ISO timestamp of first arrival */
  first_seen?: string;
  signal?: AttributionSignal;
}

function isLocalhost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  const local = isLocalhost(window.location.hostname);
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    `Max-Age=${MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (!local) {
    parts.push("Domain=.newsquawk.com");
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

/**
 * Call once on app mount. Reads UTM params from the URL and
 * document.referrer, then stores them in the shared cookie.
 *
 * First identifiable touch wins — a stored value is only replaced when
 * the stored one is referrer-only and this landing carries real UTMs.
 */
export function captureAttribution(): void {
  try {
    const existing = getAttribution();
    // A campaign capture is final; nothing later overwrites it.
    if (existing?.signal === "campaign") return;

    const params = new URLSearchParams(window.location.search);
    const attribution: Attribution = {};

    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    const utmContent = params.get("utm_content");
    const utmTerm = params.get("utm_term");

    if (utmSource) attribution.utm_source = utmSource;
    if (utmMedium) attribution.utm_medium = utmMedium;
    if (utmCampaign) attribution.utm_campaign = utmCampaign;
    if (utmContent) attribution.utm_content = utmContent;
    if (utmTerm) attribution.utm_term = utmTerm;

    const hasCampaign = Boolean(utmSource || utmMedium || utmCampaign);

    // Only an external referrer counts as a source.
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        if (!OWN_DOMAINS.includes(referrerUrl.hostname)) {
          attribution.referrer = document.referrer;
          attribution.referrer_domain = referrerUrl.hostname;
        }
      } catch {
        // Malformed referrer — skip
      }
    }

    const hasSignal = hasCampaign || Boolean(attribution.referrer_domain);
    if (!hasSignal) return;

    // Already have a referrer-only capture and this one is no stronger.
    if (existing && !hasCampaign) return;

    attribution.landing_page = (
      window.location.hostname + window.location.pathname
    ).slice(0, MAX_LANDING_PAGE_LENGTH);
    attribution.first_seen = existing?.first_seen ?? new Date().toISOString();
    attribution.signal = hasCampaign ? "campaign" : "referrer";

    writeCookie(COOKIE_NAME, JSON.stringify(attribution));
  } catch {
    // Cookie/URL parsing failure — silently ignore, tracking must never
    // break the app.
  }
}

/**
 * Returns the stored attribution data, or null if none was captured.
 */
/**
 * The fingerprint of the internal-link tagging the marketing site used to do.
 *
 * Until 2026-08-26 our own CTAs stamped `utm_source=newsquawk` and
 * `utm_medium=website` onto links into this app. Neither is a real external
 * source: `newsquawk` is us, and `website` is not a medium GA4 maps to any
 * channel. The pair is only ever something we manufactured.
 *
 * It matters here because `nq_attr` is scoped to `.newsquawk.com`, so this app
 * reads the same cookie the marketing site wrote. The marketing site stopped
 * putting the pair on URLs, but this app kept attaching it to the first
 * page_view -- which is exactly what tells GA4 the session's source. The
 * retagged share of sessions fell from ~25% to ~16% and then stopped, and the
 * remainder were sessions landing here, with no new users among them.
 */
export function isInternalRetagAttribution(a: Attribution | null | undefined): boolean {
  return a?.utm_source === 'newsquawk' && a?.utm_medium === 'website';
}

/**
 * Guarded at the read rather than at each caller. Five places consume this --
 * page tracking, login, and three in the pricing modal -- and every one of them
 * wants the same answer, so a caller-side check is four chances to miss one.
 */
export function getAttribution(): Attribution | null {
  try {
    const raw = readCookie(COOKIE_NAME);
    const stored = raw ? (JSON.parse(raw) as Attribution) : null;
    // Treated as no attribution at all rather than passed on. A wrong source
    // is worse than a blank: a blank is visibly missing, while "newsquawk /
    // website" looks like a result and has been counted as one.
    return isInternalRetagAttribution(stored) ? null : stored;
  } catch {
    return null;
  }
}
